import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import { usePageTransition } from './TransitionProvider';
import { gsap } from 'gsap';
import HorizontalScrollGallery from './HorizontalScrollGallery';
import AnimatedCopy from './AnimatedCopy';
import DecryptedText from './DecryptedText';
import HoverVideoPlayer from './HoverVideoPlayer';
import SplitText from './SplitText';
import ModelViewer from './ModelViewer';
import BrandBookFlip from './BrandBookFlip';
import { getNextProject, getProjectById } from '../data/projects';
import { getInsightBySlug } from '../data/insights';
import Transition from './Transition';
import SEO from './SEO';
import '../styles/next-project.css';
import '../styles/project-header.css';

// Default configuratie als fallback (wordt overschreven door project config)
const DEFAULT_CONFIG = {
  clientReview: {
    enabled: false,
    title: "CLIENT\nREVIEW",
    description: ""
  },
  extraImages: {
    enabled: false,
    folderPath: "/project-images",
    images: [],
    maxImages: undefined
  },
  watchVideo: {
    enabled: false,
    title: "WATCH\nTHE VIDEO",
    videoSrc: '',
    videoThumbnail: ''
  },
  horizontalGallery: {
    enabled: false,
    folderPath: "/project-images",
    images: [],
    showLabels: true
  },
  bannerImage: {
    enabled: true,
    src: '/project-images/project-img-2.svg'
  },
  modelViewer: {
    enabled: false,
    modelSrc: "",
    title: "3D MODEL"
  },
  brandBook: {
    enabled: false,
    title: "FLIP\nTHROUGH",
    pages: []
  }
};

const ProjectPage = () => {
  const { imageTransitionData, start } = usePageTransition();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [nextProject, setNextProject] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const dividerRef = useRef(null);
  const videoDividerRef = useRef(null);
  const autoNavigateTriggered = useRef(false);
  const [lightboxMedia, setLightboxMedia] = useState({
    src: null,
    alt: '',
    type: 'image',
    poster: '',
    autoPlay: true,
    loop: true,
    muted: true,
  });
  const [isLightboxVisible, setIsLightboxVisible] = useState(false);
  
  const getMediaTypeFromSrc = (src) => {
    if (!src || typeof src !== 'string') return 'image';
    const extension = src.split('.').pop()?.toLowerCase();
    return ['mp4', 'webm', 'mov'].includes(extension) ? 'video' : 'image';
  };

  const normalizeMediaInput = (input, fallbackAlt = '') => {
    if (!input) {
      return {
        src: null,
        alt: '',
        type: 'image',
        poster: '',
        autoPlay: true,
        loop: true,
        muted: true,
      };
    }

    if (typeof input === 'string') {
      return {
        src: input,
        alt: fallbackAlt,
        type: getMediaTypeFromSrc(input),
        poster: '',
        autoPlay: true,
        loop: true,
        muted: true,
      };
    }

    const src = input.src || null;
    const type = input.type || getMediaTypeFromSrc(src);

    return {
      src,
      alt: input.alt ?? fallbackAlt ?? '',
      type: type || 'image',
      poster: input.poster || input.thumbnail || '',
      autoPlay: input.autoPlay ?? true,
      loop: input.loop ?? true,
      muted: input.muted ?? true,
    };
  };

  const handleOpenLightbox = (mediaInput, fallbackAlt = '') => {
    const media = normalizeMediaInput(mediaInput, fallbackAlt);
    if (!media.src) return;
    setLightboxMedia(media);
    requestAnimationFrame(() => setIsLightboxVisible(true));
  };
  const handleCloseLightbox = () => {
    setIsLightboxVisible(false);
  };
  const renderProjectImage = (src, alt, key) => (
    <div
      key={key}
      className="relative group overflow-hidden rounded-[8px] sm:rounded-[10px] cursor-zoom-in"
      onClick={() => handleOpenLightbox({ src, alt, type: 'image' })}
    >
      <img
        src={src}
        alt={alt}
        className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-[1.02]"
      />
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          handleOpenLightbox({ src, alt, type: 'image' });
        }}
        className="absolute bottom-4 right-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/15 backdrop-blur-lg border border-white/80 text-white opacity-0 group-hover:opacity-100 transition duration-300"
        aria-label="Vergroot afbeelding"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" fill="none" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
        </svg>
      </button>
    </div>
  );
  
  // Haal configuratie op van huidig project of gebruik defaults
  const projectConfig = currentProject?.config || DEFAULT_CONFIG;
  
  // Filter en beperk images op basis van configuratie
  const extraImagesConfig = projectConfig.extraImages || DEFAULT_CONFIG.extraImages;
  const enabledImages = (extraImagesConfig.images || [])
    .filter(img => typeof img === 'object' ? img.enabled !== false : true)
    .map(img => typeof img === 'object' ? img.src : img);
  
  const displayImages = extraImagesConfig.maxImages 
    ? enabledImages.slice(0, extraImagesConfig.maxImages)
    : enabledImages;
  
  useEffect(() => {
    if (lightboxMedia.src) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxMedia.src]);

  useEffect(() => {
    if (!isLightboxVisible && lightboxMedia.src) {
      const timeout = setTimeout(() => {
        setLightboxMedia({
          src: null,
          alt: '',
          type: 'image',
          poster: '',
          autoPlay: true,
          loop: true,
          muted: true,
        });
      }, 180);
      return () => clearTimeout(timeout);
    }
  }, [isLightboxVisible, lightboxMedia.src]);
  
  // Calculate animation delay based on whether we're coming from a transition
  // Transition timing: 300ms delay + 600ms bar up + 50ms wait + 100ms delay + 600ms bar down = ~1650ms
  // Sneller starten: delay verkleinen naar 800ms
  const transitionDelay = imageTransitionData ? 800 : 0;

  // Project ID uit het pad (/project/:id) heeft voorrang. Vallen we daarop
  // terug op een legacy ?id= query (bv. oude gedeelde links), dan navigeren
  // we eenmalig door naar de nieuwe URL zodat sociale previews/SEO consistent
  // blijven met wat in de sitemap staat.
  const pathId = parseInt(params.id, 10);
  const queryId = parseInt(searchParams.get('id'), 10);
  const currentProjectId = Number.isFinite(pathId)
    ? pathId
    : Number.isFinite(queryId)
      ? queryId
      : 1;

  useEffect(() => {
    if (!Number.isFinite(pathId) && Number.isFinite(queryId)) {
      navigate(`/project/${queryId}`, { replace: true });
    }
  }, [pathId, queryId, navigate]);

  useEffect(() => {
    const project = getProjectById(currentProjectId);
    const next = getNextProject(currentProjectId);
    setCurrentProject(project);
    setNextProject(next);
    
    // Reset scroll position when project ID changes
    const scrollToTop = () => {
      const lenis = typeof window !== 'undefined' ? window.__lenis : null;
      if (lenis?.scrollTo) {
        lenis.scrollTo(0, { immediate: true, lock: true });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    };
    
    // Reset scroll immediately when project changes
    scrollToTop();
  }, [currentProjectId]);

  // State to control when animations should start
  const [animationsReady, setAnimationsReady] = useState(!imageTransitionData);

  useEffect(() => {
    // If we have transition data, wait for the transition to complete
    if (imageTransitionData) {
      // Wait for the transition from TransitionProvider to complete - sneller starten
      const timer = setTimeout(() => {
        setAnimationsReady(true);
        // Ensure scroll is at top after transition
        const lenis = typeof window !== 'undefined' ? window.__lenis : null;
        if (lenis?.scrollTo) {
          lenis.scrollTo(0, { immediate: true, lock: false });
        }
      }, 800); // Verkort van 1700ms naar 800ms

      return () => clearTimeout(timer);
    } else {
      // No transition, start animations immediately
      setAnimationsReady(true);
    }
  }, [imageTransitionData]);

  // Animeer de divider lijn van links naar rechts - alleen na transition
  useEffect(() => {
    if (dividerRef.current && animationsReady) {
      gsap.fromTo(dividerRef.current, 
        { scaleX: 0, transformOrigin: 'left' },
        { 
          scaleX: 1, 
          duration: 1.2, 
          ease: 'power3.out',
          delay: 0.2
        }
      );
    }
    if (videoDividerRef.current && animationsReady) {
      gsap.fromTo(videoDividerRef.current,
        { scaleX: 0, transformOrigin: 'left' },
        {
          scaleX: 1,
          duration: 1.2,
          ease: 'power3.out',
          delay: 0.2
        }
      );
    }
  }, [animationsReady]);

  useEffect(() => {
    if (!nextProject) return;

    const handleMobileAutoScroll = () => {
      if (autoNavigateTriggered.current) return;
      if (window.innerWidth > 768) return;
      if (window.scrollY < 150) return;

      const scrollPosition = window.scrollY + window.innerHeight;
      const threshold = document.documentElement.scrollHeight - 120;

      if (scrollPosition >= threshold) {
        autoNavigateTriggered.current = true;
        start(`/project/${nextProject.id}`);
      }
    };

    window.addEventListener('scroll', handleMobileAutoScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleMobileAutoScroll);
      autoNavigateTriggered.current = false;
    };
  }, [nextProject, start]);

  const projectImage =
    currentProject?.image && typeof currentProject.image === 'string' && currentProject.image.startsWith('http')
      ? currentProject.image
      : currentProject?.image
        ? currentProject.image
        : undefined;

  const projectJsonLd = currentProject
    ? {
        '@context': 'https://schema.org',
        '@type': 'CreativeWork',
        name: currentProject.title,
        description: currentProject.description,
        image: projectImage,
        dateCreated: currentProject.date,
        author: {
          '@type': 'Person',
          name: 'Sander Kuyken',
          url: 'https://portfolio.skdesign.be/',
        },
        keywords: currentProject.tag,
        url: `https://portfolio.skdesign.be/project/${currentProject.id}`,
      }
    : null;

  return (
    <div className="bg-[#f2f3d9] box-border relative min-h-[100vh]">
      {currentProject ? (
        <SEO
          title={currentProject.title}
          description={currentProject.description}
          path={`/project/${currentProject.id}`}
          image={projectImage}
          type="article"
          jsonLd={projectJsonLd}
        />
      ) : (
        <SEO
          title="Project not found"
          description="This project could not be found."
          path="/project"
          noIndex
        />
      )}
      <Navbar />
      
      {/* ========== OUDE HERO SECTION (IN COMMENTAAR) ==========
      <div className="flex items-center justify-center px-[1.5vw] py-[26px] mb-[80px]">
        <div className="w-[95vw] max-w-[1400px]">
          <div className="content-stretch flex flex-col items-start relative shrink-0 w-full">
            <div ref={imageContainerRef} className="h-[60vh] relative rounded-tl-[20px] rounded-tr-[20px] shrink-0 w-full">
              <div aria-hidden="true" className="absolute inset-0 pointer-events-none rounded-tl-[20px] rounded-tr-[20px]">
                <img 
                  alt={imageTransitionData?.projectData?.title || 'Project image'} 
                  className="absolute max-w-none object-cover rounded-tl-[20px] rounded-tr-[20px] size-full" 
                  src={imageTransitionData?.projectData?.image || imageTransitionData?.imageSrc}
                />
                <div ref={gradientRef} className="absolute bg-gradient-to-b from-[rgba(0,0,0,0)] inset-0 rounded-tl-[20px] rounded-tr-[20px] to-[#1e79c2]" />
              </div>
            </div>
            <div className="bg-[#1a7dcb] box-border content-stretch flex flex-col gap-[10px] min-h-[20vh] items-start px-[14px] py-[21px] relative rounded-bl-[20px] rounded-br-[20px] shrink-0 w-full">
              <div ref={textContentRef} className="content-stretch flex flex-col gap-[20px] items-start relative shrink-0 w-full">
                <div className="h-0 relative shrink-0 w-full">
                  <div className="absolute bottom-0 left-0 right-0 top-[-1px]">
                    <img alt="" className="block max-w-none size-full" src="data:image/svg+xml;base64,PHN2ZwogICAgICB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciCiAgICAgIHZpZXdCb3g9IjAgMCAxIDEiCiAgICAgIHByZXNlcnZlQXNwZWN0UmF0aW89Im5vbmUiCiAgICAgIHdpZHRoPSIxMDAlIgogICAgICBoZWlnaHQ9IjEwMCUiCiAgICA+CiAgICAgIDxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiNFRUUiIC8+CiAgICA8L3N2Zz4=" />
                  </div>
                </div>
                <div className="content-stretch flex items-end justify-between relative shrink-0 w-full">
                  <div className="content-stretch flex flex-col gap-[25px] items-start leading-[0] relative shrink-0 text-[#e4dbd2] w-[60%]">
                    <div className="font-anton relative shrink-0 text-[3.5vw] md:text-[64px] tracking-[-2.56px] w-full">
                      <p className="leading-[0.89]">GRADUAATSPROEF DIGITALE VORMGEVING</p>
                    </div>
                    <div className="font-['Space_Grotesk:Regular',_sans-serif] font-normal relative shrink-0 text-[20px] tracking-[-0.8px] w-full">
                      <p className="leading-[0.89]">{`A Smart Cup is a purpose-driven distribution CPG & Innovation company offering modern, sustainable solutions to address environmental impact. Powered by an endless circle of recycling, the brand provides businesses with customizable, `}</p>
                    </div>
                  </div>
                  <div className="content-stretch flex gap-[24px] items-center relative shrink-0">
                    <div className="box-border content-stretch flex gap-[10px] h-[37px] items-center justify-center px-[14px] py-[6px] relative rounded-[60px] shrink-0">
                      <div aria-hidden="true" className="absolute border border-[#e4dbd2] border-solid inset-0 pointer-events-none rounded-[60px]" />
                      <div className="font-['Bricolage_Grotesque:96pt_ExtraBold',_sans-serif] font-extrabold leading-[0] relative shrink-0 text-[#e4dbd2] text-[24px] text-nowrap tracking-[-0.96px]">
                        <p className="leading-[0.89] whitespace-pre">Branding</p>
                      </div>
                    </div>
                    <div className="font-['Bricolage_Grotesque:Regular',_sans-serif] font-normal leading-[0] relative shrink-0 text-[#e4dbd2] text-[36px] tracking-[-1.44px] w-[80px]">
                      <p className="leading-[0.89]">2025</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      ========== EINDE OUDE HERO SECTION ========== */}

      {/* Nieuwe Project Header Section */}
      {currentProject && (
        <section className="project-header">
          <div className="project-container">
            <div className="project-title">
              <SplitText
                text={currentProject.title}
                tag="h3"
                className="project-title-text"
                splitType="chars"
                delay={50}
                duration={0.8}
                ease="power3.out"
                from={{ opacity: 0, y: 100 }}
                to={{ opacity: 1, y: 0 }}
                threshold={0}
                rootMargin="0px"
                initialDelay={transitionDelay + 100}
              />
            </div>
            
            <div className="project-meta">
              <AnimatedCopy tag="p" className="project-meta-date" animateOnScroll={false} delay={(transitionDelay / 1000) + 0.1}>
                {currentProject.date}
              </AnimatedCopy>
              <AnimatedCopy tag="div" className="project-meta-tag" animateOnScroll={false} delay={(transitionDelay / 1000) + 0.15}>
                {currentProject.tag}
              </AnimatedCopy>
            </div>
            
            <div ref={dividerRef} className="project-header-divider"></div>
          </div>
        </section>
      )}

      {/* Banner Image - alleen tonen als enabled */}
      {projectConfig.bannerImage?.enabled && projectConfig.bannerImage?.src && (
        <section className="project-banner-img">
          <div className="project-container">
            <img src={projectConfig.bannerImage.src} alt="Project Banner" />
          </div>
        </section>
      )}

      {/* Project Overview */}
      {currentProject && (
        <section className="w-[92vw] md:w-[82vw] max-w-[2000px] mx-auto px-4 sm:px-6 md:px-8 lg:px-14 py-12 sm:py-16 lg:py-24">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-[2.5rem] sm:gap-[3rem] lg:gap-[6rem]">
            {/* Description - boven op mobiel */}
            <div className="flex-1 w-full md:w-auto order-1 md:order-2">
              <p className="font-['Space_Grotesk',_sans-serif] text-[clamp(1rem,1.8vw,2.5rem)] leading-[1.6] text-[#262624] m-0">
                {currentProject.description}
              </p>
            </div>

            {/* Meta Info */}
            <div className="flex w-full md:w-[50%] flex-col gap-6 md:gap-8 order-2 md:order-1">
              <div className="flex flex-col gap-2">
                <p className="font-['Space_Grotesk',_sans-serif] text-[clamp(0.625rem,0.9vw,0.85rem)] font-semibold uppercase tracking-[0.1em] text-[#262624] m-0">
                  <DecryptedText text="SECTOR" animateOn="view" speed={80} />
                </p>
                <p className="font-['Space_Grotesk',_sans-serif] text-[clamp(1rem,1.35vw,1.6rem)] text-[#262624] leading-[1.5] m-0">
                  {currentProject.sector}
                </p>
              </div>

              {/* Services sectie - alleen tonen als services niet leeg is */}
              {currentProject.services && currentProject.services.trim() !== "" && (
                <div className="flex flex-col gap-2">
                  <p className="font-['Space_Grotesk',_sans-serif] text-[clamp(0.625rem,0.9vw,0.85rem)] font-semibold uppercase tracking-[0.1em] text-[#262624] m-0">
                    <DecryptedText text="WHAT I DID" animateOn="view" speed={80} />
                  </p>
                  <p className="font-['Space_Grotesk',_sans-serif] text-[clamp(1rem,1.35vw,1.6rem)] text-[#262624] leading-[1.5] m-0">
                    {currentProject.services}
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <p className="font-['Space_Grotesk',_sans-serif] text-[clamp(0.625rem,0.9vw,0.85rem)] font-semibold uppercase tracking-[0.1em] text-[#262624] m-0">
                  <DecryptedText text="TECHNOLOGIES" animateOn="view" speed={80} />
                </p>
                <p className="font-['Space_Grotesk',_sans-serif] text-[clamp(1rem,1.35vw,1.6rem)] text-[#262624] leading-[1.5] m-0">
                  {currentProject.technologies}
                </p>
              </div>

              {currentProject.siteUrl && (
                <div>
                  <a
                    href={currentProject.siteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover-effect inline-flex items-center gap-2 font-['Space_Grotesk',_sans-serif] text-[clamp(0.875rem,1vw,1.1rem)] font-semibold text-[#262624]"
                  >
                    <span>&#9654;</span> Visit Site
                  </a>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Case study: challenge / approach / result (Fase 5) — only renders for
          projects that provide a `caseStudy` object, so the rest of the grid
          stays untouched. Qualitative outcomes only, no invented metrics. */}
      {currentProject?.caseStudy && (
        <section className="w-[92vw] md:w-[82vw] max-w-[2000px] mx-auto px-4 sm:px-6 md:px-8 lg:px-14 pb-4 sm:pb-8 lg:pb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 lg:gap-14 border-t border-[#262624]/15 pt-12 sm:pt-16">
            {[
              { label: 'The challenge', body: currentProject.caseStudy.challenge },
              { label: 'The approach', body: currentProject.caseStudy.approach },
              { label: 'The result', body: currentProject.caseStudy.result },
            ].filter((b) => b.body).map((block, i) => (
              <div key={block.label} className="flex flex-col gap-4">
                <span className="font-['Space_Grotesk',_sans-serif] text-[clamp(0.625rem,0.9vw,0.85rem)] font-semibold uppercase tracking-[0.18em] text-[#262624]/55 flex items-center gap-2.5">
                  <span className="font-anton text-[#f97b22] text-[18px] leading-none">0{i + 1}</span>
                  {block.label}
                </span>
                <p className="font-['Space_Grotesk',_sans-serif] text-[clamp(1rem,1.25vw,1.4rem)] text-[#262624] leading-[1.5] m-0">
                  {block.body}
                </p>
              </div>
            ))}
          </div>

          {(currentProject.caseStudy.role || (currentProject.caseStudy.deliverables || []).length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-12 sm:mt-14 pt-10 border-t border-[#262624]/15">
              {currentProject.caseStudy.role && (
                <div className="flex flex-col gap-2">
                  <span className="font-['Space_Grotesk',_sans-serif] text-[clamp(0.625rem,0.9vw,0.85rem)] font-semibold uppercase tracking-[0.18em] text-[#262624]/55">
                    My role
                  </span>
                  <p className="font-['Space_Grotesk',_sans-serif] text-[clamp(1rem,1.1vw,1.25rem)] text-[#262624] leading-[1.5] m-0">
                    {currentProject.caseStudy.role}
                  </p>
                </div>
              )}
              {(currentProject.caseStudy.deliverables || []).length > 0 && (
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <span className="font-['Space_Grotesk',_sans-serif] text-[clamp(0.625rem,0.9vw,0.85rem)] font-semibold uppercase tracking-[0.18em] text-[#262624]/55">
                    Deliverables
                  </span>
                  <div className="flex flex-wrap gap-2.5 mt-1">
                    {currentProject.caseStudy.deliverables.map((d) => (
                      <span key={d} className="font-['Space_Grotesk',_sans-serif] text-[14px] text-[#262624] border border-[#262624]/25 rounded-full px-3.5 py-1.5">
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Horizontale Scroll Galerie Sectie - alleen tonen als enabled */}
      {projectConfig.horizontalGallery?.enabled && (
        <HorizontalScrollGallery 
          images={projectConfig.horizontalGallery?.images || []}
          showLabels={projectConfig.horizontalGallery?.showLabels ?? false}
          enableLightbox
          onImageClick={(media) => handleOpenLightbox(media)}
        />
      )}

      {/* Client Review Sectie - alleen tonen als enabled */}
      {projectConfig.clientReview?.enabled && (
        <section className="project-review mt-[40px] sm:mt-[60px] md:mt-[80px] mb-[40px] sm:mb-[50px] md:mb-[60px]">
          <div className="project-container">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[2rem] sm:gap-[1rem] md:gap-[1.5rem] items-start">
              {/* Linker kolom - Titel */}
              <div className="flex flex-col">
                <div className="text-[#262624]">
                  {projectConfig.clientReview.title?.split('\n').map((line, index) => (
                    <p key={index} className="client-review-heading">{line}</p>
                  ))}
                </div>
              </div>

              {/* Rechter kolom - Uitleg */}
              {projectConfig.clientReview.description && (
                <div className="flex items-start sm:mt-0 md:mt-2">
                  <p className="client-review-text">
                    {projectConfig.clientReview.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Image Grid Section - alleen tonen als enabled */}
      {extraImagesConfig.enabled && displayImages.length > 0 && (
        <section className="project-images">
          <div className="project-container">
            {displayImages.length >= 2 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[15px] sm:gap-[20px] mb-[15px] sm:mb-[20px]">
                {renderProjectImage(displayImages[0], 'Project image 1', 'project-grid-1')}
                {displayImages[1] && renderProjectImage(displayImages[1], 'Project image 2', 'project-grid-2')}
              </div>
            )}
            {displayImages.length >= 3 && (
              <div className="w-full">
                {renderProjectImage(displayImages[2], 'Project image 3', 'project-grid-3')}
              </div>
            )}
            {/* Extra images in een grid layout */}
            {displayImages.length > 3 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[15px] sm:gap-[20px] mt-[15px] sm:mt-[20px]">
                {displayImages.slice(3).map((img, index) => 
                  renderProjectImage(img, `Project image ${index + 4}`, `project-grid-${index + 4}`)
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* 3D Model Viewer Section - alleen tonen als enabled */}
      {projectConfig.modelViewer?.enabled && projectConfig.modelViewer?.modelSrc && (
        <section className="project-model-viewer" style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)',
          padding: '0 2.75rem',
          marginTop: 'clamp(48px, 8vw, 96px)',
          marginBottom: 'clamp(48px, 8vw, 96px)',
        }}>
          <div className="project-container">
            <div className="watch-video-header mb-4 sm:mb-6 md:mb-8">
              <div className="watch-video-title text-[#262624]">
                {(projectConfig.modelViewer.title || '3D MODEL').split('\n').map((line, index) => (
                  <p key={index} className={index === 0 ? "mb-0 leading-[1]" : "leading-[0.89]"}>{line}</p>
                ))}
              </div>
              <div className="project-header-divider"></div>
            </div>
            <ModelViewer modelSrc={projectConfig.modelViewer.modelSrc} />
          </div>
        </section>
      )}

      {/* Brand Book / Flip-through Section - alleen tonen als enabled */}
      {projectConfig.brandBook?.enabled && projectConfig.brandBook?.pages?.length > 0 && (
        <section className="project-brandbook mb-[40px] sm:mb-[60px] md:mb-[80px] mt-[40px] sm:mt-[60px] md:mt-[80px]">
          <div className="project-container">
            <div className="watch-video-header mb-4 sm:mb-6 md:mb-8">
              <div className="watch-video-title text-[#262624]">
                {(projectConfig.brandBook.title || 'FLIP\nTHROUGH').split('\n').map((line, index) => (
                  <p key={index} className={index === 0 ? "mb-0 leading-[1]" : "leading-[0.89]"}>{line}</p>
                ))}
              </div>
              <div className="project-header-divider"></div>
            </div>
            <BrandBookFlip pages={projectConfig.brandBook.pages} />
          </div>
        </section>
      )}

      {/* Video Player Section - alleen tonen als enabled */}
      {projectConfig.watchVideo?.enabled && projectConfig.watchVideo?.videoSrc && (
        <section className="project-video mb-[40px] sm:mb-[60px] md:mb-[80px] mt-[40px] sm:mt-[60px] md:mt-[80px]">
          <div className="project-container">
            <div className="watch-video-header mb-4 sm:mb-6 md:mb-8">
              <div className="watch-video-title text-[#262624]">
                {projectConfig.watchVideo.title?.split('\n').map((line, index) => (
                  <p key={index} className={index === 0 ? "mb-0 leading-[1]" : "leading-[0.89]"}>{line}</p>
                ))}
              </div>
              <div ref={videoDividerRef} className="project-header-divider"></div>
            </div>
            <div 
              className="mx-auto w-full cursor-pointer"
              onClick={() => handleOpenLightbox({
                src: projectConfig.watchVideo.videoSrc,
                alt: 'Project Video',
                type: 'video',
                poster: projectConfig.watchVideo.videoThumbnail || '',
                autoPlay: true,
                loop: false,
                muted: false
              })}
            >
              <div className="relative group">
                <HoverVideoPlayer
                  videoSrc={projectConfig.watchVideo.videoSrc}
                  thumbnailSrc={projectConfig.watchVideo.videoThumbnail || ''}
                  enableControls={false}
                  className="rounded-[10px] sm:rounded-[15px] md:rounded-[20px] w-full aspect-video"
                />
                <button
                  type="button"
                  className="absolute bottom-4 right-4 inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-white/15 backdrop-blur-lg border border-white/80 text-white opacity-0 group-hover:opacity-100 transition duration-300"
                  aria-label="Play video fullscreen"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenLightbox({
                      src: projectConfig.watchVideo.videoSrc,
                      alt: 'Project Video',
                      type: 'video',
                      poster: projectConfig.watchVideo.videoThumbnail || '',
                      autoPlay: true,
                      loop: false,
                      muted: false
                    });
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {lightboxMedia.src && (
        <div
          className={`fixed inset-0 z-[1000] flex items-center justify-center bg-black/65 backdrop-blur-[30px] backdrop-saturate-150 p-6 transition-opacity duration-200 ${isLightboxVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          onClick={handleCloseLightbox}
        >
          <div
            className="relative flex flex-col items-center justify-center w-full sm:w-[90vw] md:w-[85vw] lg:w-[80vw] xl:w-[70vw] max-w-[1200px] max-h-[92vh]"
            onClick={(event) => event.stopPropagation()}
          >
            {lightboxMedia.type === 'video' ? (
              <video
                src={lightboxMedia.src}
                poster={lightboxMedia.poster || ''}
                className="block w-full h-auto max-h-[90vh] object-contain rounded-[22px] shadow-[0_25px_60px_rgba(0,0,0,0.45)]"
                controls
                autoPlay={lightboxMedia.autoPlay}
                loop={lightboxMedia.loop}
                muted={lightboxMedia.muted}
                playsInline
              />
            ) : (
              <img
                src={lightboxMedia.src}
                alt={lightboxMedia.alt || 'Vergrote projectafbeelding'}
                className="block w-full h-auto max-h-[90vh] object-contain rounded-[22px] shadow-[0_25px_60px_rgba(0,0,0,0.45)]"
              />
            )}
            <button
              type="button"
              onClick={handleCloseLightbox}
              className="mt-6 inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/15 backdrop-blur-lg border border-white/80 text-white hover:bg-white/25 transition"
              aria-label="Sluit vergrote afbeelding"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" fill="none" className="w-7 h-7">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Related thinking / capability — subtle, portfolio-first */}
      {currentProject?.relatedLinks && (() => {
        const related = currentProject.relatedLinks;
        const relatedInsight = getInsightBySlug(related.insightSlug);
        return (
          <section
            aria-label="Related thinking and capability"
            className="project-related mt-[40px] sm:mt-[60px] md:mt-[80px] mb-[10px] sm:mb-[20px]"
          >
            <div className="project-container">
              <div className="border-t border-[#262624]/15 pt-8 sm:pt-10">
                <p className="font-space text-[#262624]/55 text-xs uppercase tracking-[0.22em] mb-5 sm:mb-6">
                  Related
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                  {relatedInsight && (
                    <Link
                      to={`/insights/${relatedInsight.slug}`}
                      className="group flex flex-col gap-2 bg-white/45 hover:bg-white border border-[#262624]/15 hover:border-[#262624]/35 rounded-2xl p-5 sm:p-6 transition-all duration-300"
                    >
                      <span className="font-space text-[#262624]/50 text-[11px] uppercase tracking-[0.2em]">
                        Related thinking
                      </span>
                      <span className="font-bricolage font-bold text-[#262624] text-[19px] sm:text-[22px] leading-[1.15] group-hover:text-[#f97b22] transition-colors duration-300">
                        {relatedInsight.title}
                      </span>
                    </Link>
                  )}
                  {related.servicePath && (
                    <Link
                      to={related.servicePath}
                      className="group flex flex-col gap-2 bg-white/45 hover:bg-white border border-[#262624]/15 hover:border-[#262624]/35 rounded-2xl p-5 sm:p-6 transition-all duration-300"
                    >
                      <span className="font-space text-[#262624]/50 text-[11px] uppercase tracking-[0.2em]">
                        Relevant capability
                      </span>
                      <span className="font-bricolage font-bold text-[#262624] text-[19px] sm:text-[22px] leading-[1.15] group-hover:text-[#f97b22] transition-colors duration-300">
                        {related.label}
                      </span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Next Project Section */}
        {nextProject && (
          <Link to={`/project/${nextProject.id}`} className="next-project-link">
          <section className="next-project">
            <AnimatedCopy tag="h2" animateOnScroll={true} className="next-project-title">
              NEXT
            </AnimatedCopy>

            <div className="next-project-color-block" style={{ backgroundColor: nextProject.color }}>
              <img
                src={nextProject.hoverImage ?? nextProject.image}
                alt={nextProject.title}
                className="w-full h-full object-cover rounded-[1rem]"
              />
            </div>

            <AnimatedCopy tag="h2" animateOnScroll={true} className="next-project-title">
              {nextProject.shortTitle.toUpperCase()}
            </AnimatedCopy>
          </section>
        </Link>
      )}
    </div>
  );
};

export default Transition(ProjectPage);
